import React, {
    useState, useContext, useCallback, useEffect, useMemo
} from 'react';
import _ from 'lodash';
import { withRouter } from 'react-router-dom';
import { ModalNextContext, Tooltip } from '@jutro/components';
import {  LocalDateUtil } from '@xengage/gw-portals-util-js';
import moment from 'moment';
import { useTranslator } from '@jutro/locale';
import { WizardPage, wizardProps } from '@xengage/gw-portals-wizard-react';
import { DigitalWizardCustPageTemplate } from 'gw-portals-wizard-components-ui';
import { useDependencies } from '@xengage/gw-portals-dependency-react';
import { withAuthenticationContext } from '@xengage/gw-digital-auth-react';
import { claimsMessages } from 'gw-capability-claim-react';
import { messages as commonMessages } from '@xengage/gw-platform-translations';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { ViewModelServiceContext, ViewModelForm } from '@xengage/gw-portals-viewmodel-react';
import metadata from './AdditionalInformationPage.metadata.json5';
import styles from './AdditionalInformationPage.module.scss';
import  messages from  '../../FNOL.messages';
import Claim from '../../models/Claim';
import usecpmiAnalytics from '../../../../hooks/usecpmiAnalytics'
import useGoogleAnalytics from '../../../../hooks/useGoogleAnalytics';

import { IconButton, Button, InputField, DropdownSelectField, PhoneNumberField } from '@jutro/legacy/components';

const ROLES_TO_FILTER = ['witness', 'other'];

const getPhoneFieldBasedOnPrimaryPhoneType = (relatedContact) => {
    let primaryPhoneType;
    switch (relatedContact.contact.primaryPhoneType) {
        case 'home':
            primaryPhoneType = 'homeNumber';
            break;
        case 'work':
            primaryPhoneType = 'workNumber';
            break;
        case 'mobile':
            primaryPhoneType = 'cellNumber';
            break;
        default:
            primaryPhoneType = 'homeNumber';
    }
    return primaryPhoneType;
};

function FNOLAdditionalInformationPage(props) {
    const {
        wizardData: claimVM, updateWizardData, authHeader, userInfo
    } = props;
    const { ClaimDownloadService } = useDependencies('ClaimDownloadService');
    const { ClaimService } = useDependencies('ClaimService');
    const { FNOLService } = useDependencies('FNOLService');
    const translator = useTranslator();
    const viewModelService = useContext(ViewModelServiceContext);
    const [claimObj, updateClaimObj] = useState({});
    const [showLoader, setShowLoader] = useState(false);
    const [uploadLoader,setUploadLoader]=useState(false);
    const [disableNext,setDisableNext]=useState(false);
    const { onValidate, isComponentValid, registerComponentValidation } = useValidation(
        'FNOLAdditionalInformationPage'
    );
    const [trackPageView] = usecpmiAnalytics();
    const [trackGAPageView] = useGoogleAnalytics();
    const ModalNext=useContext(ModalNextContext);

    const showInjuredColumn = useMemo(
        () => !['InlandMarine', 'GeneralLiability', 'BusinessOwners'].includes(_.get(claimVM, 'policy.policyType.value')),
        // Policy type won't change on re-renders
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const relatedContactDisable = useCallback(async () => {
        setDisableNext(false)

        if(claimVM.value.relatedContacts !== undefined && claimVM.value.relatedContacts.length > 0){            
            claimVM.value.relatedContacts.forEach((item) => {            
                if(item.contact.firstName === undefined || item.contact.firstName === "" || item.contact.lastName === undefined || item.contact.lastName === ""){
                    setDisableNext(true)
                }
            })
        }
   

    }, [claimVM, setDisableNext]);

    const writeValue = useCallback(
        (value, path) => {
            _.set(claimVM, path, value);
            updateWizardData(claimVM);
            _.set(claimObj, 'relatedContacts', claimVM.relatedContacts.value);
            relatedContactDisable()
        },
        [claimObj, claimVM, relatedContactDisable, updateWizardData]
    );

    const handleError = useCallback(
        (title, message) => {
            return ModalNext.showAlert({
                title: title,
                message: message,
                status: 'error',
                icon: 'mi-error-outline',
                confirmButtonText: commonMessages.ok
            }).catch(_.noop);
        }, []
    );

    useEffect(() => {
        updateClaimObj(new Claim(claimVM.value));
        relatedContactDisable()
        // execute once
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        trackPageView("Additional Information","/additional-information")
    },[])

    useEffect(() => {
        trackGAPageView("/additional-information","Additional Information")
    },[])

    const getFirstLastNameData = useCallback(
        (item, rowId, property) => {
            const { id, path } = property;
            const claimVMPath = `relatedContacts.value[${rowId}].${path}`;
            // const setDisable = item.contact.publicID
            //     ? 'disabled'
            //     : '';
            return (
                <InputField
                    id={`${id}_${rowId}`}
                    className={styles.tableColumn}
                    path={claimVMPath}
                    onValueChange={writeValue}
                    value={_.get(claimVM, claimVMPath)}
                    // disabled={setDisable}
                />
            );
        },
        [claimVM, writeValue]
    );

    const filteredRoleTypeList = useMemo(() => {
        const typeList = viewModelService.create(
            {},
            'cc',
            'edge.capabilities.claim.fnol.dto.FnolRelatedContactDTO'
        );
        const filteredList = typeList.role.aspects.availableValues
            .filter((typeCode) => {
                return ROLES_TO_FILTER.includes(typeCode.code);
            })
            .map((typeCode) => {
                return {
                    code: typeCode.code,
                    name: translator({
                        id: typeCode.name,
                        defaultMessage: typeCode.name
                    })
                };
            });
        return filteredList;
    }, [translator, viewModelService]);

    const getInvolvementInjuredData = useCallback(
        (item, rowId, property) => {
            const { id, path } = property;
            let availableValues;
            const claimVMPath = `relatedContacts.value[${rowId}].${path}`;
            if (id === 'role') {
                availableValues = filteredRoleTypeList;
            } else {
                availableValues = [
                    {
                        code: 'true',
                        name: translator(messages.fnolYes)
                    },
                    {
                        code: 'false',
                        name: translator(messages.fnolNo)
                    }
                ];
            }
            return (
                <DropdownSelectField
                    id={`${id}_${rowId}`}
                    alwaysShowPlaceholder={false}
                    availableValues={availableValues}
                    path={claimVMPath}
                    onValueChange={writeValue}
                    value={_.get(claimVM, claimVMPath)}
                    className={styles.involvmentInjuredDropDown}
                />
            );
        },
        [claimVM, filteredRoleTypeList, translator, writeValue]
    );

    const onNext = useCallback(() => {
        // setShowLoader(true);
        const newClaimVM = Object.assign( {}, claimVM.value )
        _.set(newClaimVM, 'documents', []);
        return FNOLService.saveFNOLDetails(newClaimVM, authHeader)
            .then((response) => {
                claimVM.value = response;
                return claimVM;
            })
            .catch(() => {
                ModalNext.showAlert({
                    title: messages.fnolSaveDraftErrorTitle,
                    message: messages.fnolSaveDraftErrorMessage,
                    status: 'error',
                    icon: 'mi-error-outline',
                    confirmButtonText: commonMessages.ok
                }).catch(_.noop);
                return false;
            })
            .finally(() => {
                // setShowLoader(false);
            });
    }, [FNOLService, ModalNext, authHeader, claimVM]);

    const getPhoneNumberData = useCallback(
        (item, rowId) => {
            const phoneField = getPhoneFieldBasedOnPrimaryPhoneType(item);
            const claimVMPath = `relatedContacts.value[${rowId}].contact.${phoneField}`;
            // const setDisable = item.contact.publicID
            //     ? 'disabled'
            //     : '';
            return (
                <PhoneNumberField 
                    alwaysShowMask
                    id={`${item.primaryPhoneID}_${rowId}`}
                    dataType="string"
                    onValueChange={writeValue}
                    path={claimVMPath}
                    // disabled={setDisable}
                    value={_.get(claimVM, claimVMPath)}
                />
            );
        },
        [claimVM, writeValue]
    );

    const showDocumentDeleteIcon = useCallback((documentUploadItem) => {
        if (documentUploadItem.canDelete) {
            return true;
        }

        return false;
    }, []);

    const onDeleteDocumentIcon = useCallback(
        (e, item) => {
            e.preventDefault();
            const { claimNumber, publicID, name } = item;

            ModalNext.showConfirm({
                title: claimsMessages.confirmationToRemoveTitle,
                message: translator(messages.fnolRemoveUploadedDocument, { documentName: name }),
                status: 'warning',
                icon: 'mi-error-outline',
                confirmButtonText: claimsMessages.removeConfirmationYes,
                cancelButtonText: claimsMessages.removeConfirmationNo
            }).then(async (results) => {
                if (results === 'cancel') {
                    return _.noop();
                }
                setShowLoader(true);
                try {
                    const isDeleteItem = await ClaimService.claimsRemoveDocument(
                        [claimNumber, publicID],
                        authHeader
                    );
                    if (isDeleteItem === false) {
                        setShowLoader(false);
                        return handleError(
                            claimsMessages.deletionFailedTitle,
                            claimsMessages.deletionFailedMessage
                        );
                    }
                    const documentsArray = _.get(claimVM, 'documents.value');
                    const updatedDocArray = documentsArray.filter((documentObj) => {
                        return documentObj.publicID !== publicID;
                    });
                    const newClaimVM = _.clone(claimVM);
                    _.set(newClaimVM, 'documents.value', updatedDocArray);
                    updateWizardData(newClaimVM);
                    setShowLoader(false);
                    return true;
                } catch (documentDeletionError) {
                    setShowLoader(false);
                    return handleError(
                        claimsMessages.removeServiceFailedTitle,
                        claimsMessages.removeServiceFailedMessage
                    );
                }
            }, _.noop);
        },
        [ModalNext, translator, ClaimService, authHeader, claimVM, updateWizardData, handleError]
    );

    const handleDocumentDownload = useCallback(
        (item) => {
            if (_.isNil(item)) {
                return null;
            }
            
            const { docUID, sessionID } = item;
            return ClaimDownloadService
                .getClaimDocument(docUID, sessionID);
        },
        [ClaimDownloadService]
    );

    const generateMoreInfoDocumentsOverrides = useCallback(() => {
        const documentUploadPath = 'value.documents';
        const documentUploadItems = _.get(claimVM, documentUploadPath, []);
        const overrides = documentUploadItems.map((documentUploadItem, index) => {
            return {
                [`documentNameLink${index}`]: {
                    href: '#',
                    onClick: e => {
                        e.preventDefault();
                        handleDocumentDownload(documentUploadItem);
                    },
                    content: documentUploadItem.name,
                },
                [`documentDeleteIcon${index}`]: {
                    onClick: (e) => {
                        onDeleteDocumentIcon(e, documentUploadItem);
                    },
                    visible: showDocumentDeleteIcon(documentUploadItem)
                }
            };
        });
        return Object.assign({}, ...overrides);
    }, [
        claimVM,
        handleDocumentDownload,
        onDeleteDocumentIcon,
        showDocumentDeleteIcon
    ]);

    const getContactsRelatedPersons = useCallback((contactsItem) => {
        if (contactsItem.subtype === 'Person' && contactsItem.lastName) {
            return `${contactsItem.firstName} ${contactsItem.lastName}`;
        }
        return '';
    }, []);

    const generateMoreInfoAddPersonOverrides = useCallback(() => {
        const contactsPath = 'value.contacts';
        const contactsItems = _.get(claimVM, contactsPath, []);
        let availableRelatedContacts = [];
        if (!_.isEmpty(claimObj) && typeof claimObj?.availableRelatedContacts === "function") {
            availableRelatedContacts = claimObj?.availableRelatedContacts()?.filter((contact) => {
                return contact.subtype === 'Person' && contact.firstName;
            });
        }

        if (!contactsItems.length) {
            return {};
        }
        const overrides = contactsItems.map((contactItem, index) => {
            return {
                [`contactItemsDropDown${index}`]: {
                    content: getContactsRelatedPersons(contactItem),
                    visible: _.includes(availableRelatedContacts, contactItem)
                }
            };
        });
        return Object.assign({}, ...overrides);
    }, [claimVM, claimObj, getContactsRelatedPersons]);

    const removeContactItem = useCallback(
        (e, rowId) => {
            e.preventDefault();
            const { relatedContacts } = claimVM;
            const relatedContactToDelete = relatedContacts.value.filter((relatedContact, index) => {
                return index === rowId;
            });
            claimObj.removeRelatedContact(relatedContactToDelete[0].contact);
            _.set(claimVM.value, 'relatedContacts', claimObj.relatedContacts);
            updateWizardData(claimVM);
            relatedContactDisable()
        },
        [claimVM, claimObj, updateWizardData, relatedContactDisable]
    );

    const removeContactRow = useCallback(
        (item, index) => {
            return (
                <div>
                    <Tooltip
                        placement="bottom"
                        trigger="mouseenter"
                        content={translator(messages.fnolDelete)}
                    >
                        <IconButton 
                            className={styles.accountIcon}
                            icon="gw-delete"

                            onClick={(e) => removeContactItem(e, index, item)}
                        />
                    </Tooltip>
                </div>
                
            );
        },
        [removeContactItem, translator]
    );

    const generateNewWitnessRow = useCallback(
        (event) => {
            event.preventDefault();
            const { contacts } = claimVM;
            const getCurrentMenuItemID = event.currentTarget.id;
            if (getCurrentMenuItemID.indexOf('anotherPersonLink') === -1) {
                const getIndex = parseInt(getCurrentMenuItemID.match(/[0-9]/gi), 10);
                const selectedContact = contacts.value[getIndex];
                claimObj.addRelatedContact(selectedContact);
                _.set(claimVM.value, 'relatedContacts', claimObj.relatedContacts);
            } else {
                claimObj.addRelatedContact(null);
                _.set(claimVM.value, 'relatedContacts', claimObj.relatedContacts);
            }
            updateWizardData(claimVM);
            relatedContactDisable()
        },
        [claimVM, updateWizardData, claimObj]
    );

    const generateAddPersonMenuDropDown = useCallback(
        (dropDownProps, toggleMenu) => {
            const { isOpen } = dropDownProps;
            const onAddPersonClick = () => {
                toggleMenu(!isOpen);
            };
            return (
                <Button onClick={onAddPersonClick} icon="mi-expand_more" iconPosition="right" className={styles.addParty}>
                    {translator(messages.fnolAddPerson)}
                </Button>
            );
        },
        [translator]
    );

    const claimsDocUploadToken = useCallback(async () => {
        try {
            const uploadTokenID = await ClaimService.claimsDocUploadToken([], authHeader);
            return uploadTokenID;
        } catch (e) {
            return handleError(
                commonMessages.errorUploadTitle,
                commonMessages.errorGenerateUploadToken
            );
        }
    }, [ClaimService, authHeader, handleError]);

    

    const onUploadDocument = useCallback(
      
        async (file) => {
            setUploadLoader(true);
            const documentMetaDataTemplate = {
                DocUID: '001',
                DocumentType: 'fnol',
                SecurityType: 'unrestricted',
                Status: 'approved',
                Author: userInfo?.email,
                claimNumber: claimVM.claimNumber.value,
                name: file.name,
                mimeType: file.type,
                sessionID: await claimsDocUploadToken()
            };
            try {
                const data = await ClaimService.uploadDocument(
                    file,
                    documentMetaDataTemplate,
                    authHeader
                );
                const dateModified1=(moment(moment.utc(LocalDateUtil.today())).format('MM/DD/YYYY'));
                let temp = moment(dateModified1).format()
                temp = `${temp.split('T')[0].toString()}T00:00:00+00:00`
                data.dateModified=temp
               // documentsArray.push(data);
                const documentsArray = _.get(claimVM, 'documents.value') || [];
                documentsArray.push(data);
                const newClaimVM = _.clone(claimVM);
                _.set(newClaimVM, 'documents.value', documentsArray);
                updateWizardData(newClaimVM);
                setUploadLoader(false);
            } catch (error) {
                setUploadLoader(false);
                handleError(commonMessages.errorUploadTitle, commonMessages.uploadFailedMessage);
            }
        },
        [claimVM, claimsDocUploadToken, ClaimService, authHeader, updateWizardData, handleError]
    );

    const validateTableForm = useCallback(() => {
        if (
            !claimVM.relatedContacts.aspects.valid
            || !claimVM.relatedContacts.aspects.subtreeValid
        ) {
            return false;
        }
        
        return true;
    }, [claimVM]);

    const overrides = {
        addtionalInformationLoader: {
            
            visible:uploadLoader,
            customClass:"CoveragesLoader"
        },
        uploadDocumentGrid: {
            visible: !showLoader
        },
        withnessOtherPartiesContainer:{
            visible : !_.get(claimVM.value,'onFastTrack')
        },
        witnessPartyTable: {
            data: claimVM.relatedContacts.value,
            visible: !_.isEmpty(claimVM.relatedContacts.value)
        },
        injuredHeader: {
            visible: showInjuredColumn
        },
        ...generateMoreInfoDocumentsOverrides(),
        ...generateMoreInfoAddPersonOverrides()
    };
    const resolvers = {
        resolveClassNameMap: styles,
        resolveCallbackMap: {
            generateWitnessPartyTableRow: generateNewWitnessRow,
            getFirstNameData: getFirstLastNameData,
            getLastNameData: getFirstLastNameData,
            getInvolvementData: getInvolvementInjuredData,
            getPhoneNumberData: getPhoneNumberData,
            getInjuredData: getInvolvementInjuredData,
            removeContactRow: removeContactRow,
            onUploadDocument: onUploadDocument,
            renderTrigger: generateAddPersonMenuDropDown
        }
    };

    useEffect(() => {
        registerComponentValidation(validateTableForm);
    }, [registerComponentValidation, validateTableForm]);

    return (
        <WizardPage
            cancelLabel={translator(messages.fnolSaveandExit)}
            disableNext={!isComponentValid || disableNext}
            template={DigitalWizardCustPageTemplate}
            onNext={onNext}
        >
            <ViewModelForm
                uiProps={metadata.pageContent}
                model={claimVM}
                overrideProps={overrides}
                onModelChange={updateWizardData}
                onValueChange={writeValue}
                onValidationChange={onValidate}
                callbackMap={resolvers.resolveCallbackMap}
                classNameMap={resolvers.resolveClassNameMap}
            />
        </WizardPage>
    );
}
FNOLAdditionalInformationPage.propTypes = wizardProps;
export default withRouter(withAuthenticationContext(FNOLAdditionalInformationPage));